import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { absencesService, userService, workScheduleService } from '../services/api';
import { AlertTriangle, Plus, Edit, Trash2, Save, X, CheckCircle, XCircle, Calendar, User, Clock, FileText, Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmModal from './ConfirmModal';

// Função helper para parsear datas de forma segura
const parseDateTime = (dateStr) => {
  if (!dateStr) return '';
  try {
    // Se já for string vazia, retorna vazio
    if (!dateStr.trim()) return '';

    // Se já contém 'T', assume formato ISO
    if (dateStr.includes('T')) {
      const [datePart, timePart] = dateStr.split('T');
      const time = timePart?.substring(0, 5) || '';
      return time ? `${datePart}T${time}` : datePart;
    }

    // Se não contém 'T', retorna como está
    return dateStr;
  } catch (error) {
    console.error('Erro ao parsear data:', error);
    return '';
  }
};

const AbsencesManagement = () => {
  const queryClient = useQueryClient();
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAbsence, setEditingAbsence] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [deleteAbsence, setDeleteAbsence] = useState(null);
  const [formData, setFormData] = useState({
    date: '',
    type: 'FULL_DAY',
    hours: '',
    days: '',
    period_start: '',
    period_end: '',
    justified: false,
    notes: '',
  });

  // Buscar funcionários
  const { data: usersData } = useQuery({
    queryKey: ['users-for-absences'],
    queryFn: () => userService.getUsers({ limit: 100 }),
    staleTime: 5 * 60 * 1000,
  });

  const users = useMemo(() => {
    if (!usersData) return [];
    if (Array.isArray(usersData?.data?.users)) return usersData.data.users;
    if (Array.isArray(usersData?.data?.data?.users)) return usersData.data.data.users;
    if (Array.isArray(usersData?.data)) return usersData.data;
    return [];
  }, [usersData]);

  // Buscar jornadas do funcionário selecionado
  const { data: schedulesData } = useQuery({
    queryKey: ['work-schedules', selectedUserId],
    queryFn: () => workScheduleService.getWorkSchedules({ user_id: selectedUserId, is_active: true }),
    enabled: !!selectedUserId,
    staleTime: 5 * 60 * 1000,
  });

  const schedules = useMemo(() => {
    if (!schedulesData?.data?.data) return [];
    return Array.isArray(schedulesData.data.data) ? schedulesData.data.data : [];
  }, [schedulesData]);

  // Buscar divergências
  const { data: absencesData, isLoading, refetch } = useQuery({
    queryKey: ['absences', selectedUserId, selectedYear, selectedMonth],
    queryFn: () =>
      selectedUserId
        ? absencesService.getAbsences({
            userId: selectedUserId,
            year: selectedYear,
            month: selectedMonth,
          })
        : Promise.resolve({ data: [] }),
    enabled: !!selectedUserId,
    staleTime: 0,
  });

  const absences = useMemo(() => {
    if (!absencesData?.data?.data) return [];
    return Array.isArray(absencesData.data.data) ? absencesData.data.data : [];
  }, [absencesData]);

  // Mutação para criar divergência
  const createMutation = useMutation({
    mutationFn: ({ data, file }) => absencesService.createAbsence(data, file),
    onSuccess: () => {
      toast.success('Divergência registrada com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['absences'] });
      queryClient.invalidateQueries({ queryKey: ['payslips'] });
      setIsModalOpen(false);
      resetForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao registrar divergência');
    },
  });

  // Mutação para atualizar divergência
  const updateMutation = useMutation({
    mutationFn: ({ id, data, file }) => absencesService.updateAbsence(id, data, file),
    onSuccess: () => {
      toast.success('Divergência atualizada com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['absences'] });
      queryClient.invalidateQueries({ queryKey: ['payslips'] });
      setIsModalOpen(false);
      setEditingAbsence(null);
      resetForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao atualizar divergência');
    },
  });

  // Mutação para deletar divergência
  const deleteMutation = useMutation({
    mutationFn: (id) => absencesService.deleteAbsence(id),
    onSuccess: () => {
      toast.success('Divergência deletada com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['absences'] });
      queryClient.invalidateQueries({ queryKey: ['payslips'] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Erro ao deletar divergência');
    },
  });

  const resetForm = () => {
    setFormData({
      date: '',
      type: 'FULL_DAY',
      hours: '',
      days: '',
      period_start: '',
      period_end: '',
      justified: false,
      notes: '',
    });
    setSelectedFile(null);
    setEditingAbsence(null);
  };

  const handleOpenModal = (absence = null) => {
    if (absence) {
      setEditingAbsence(absence);
      setFormData({
        date: absence.date ? absence.date.split('T')[0] : '',
        type: absence.type,
        hours: absence.hours || '',
        days: absence.days || '',
        period_start: parseDateTime(absence.period_start),
        period_end: parseDateTime(absence.period_end),
        justified: absence.justified || false,
        notes: absence.notes || '',
      });
      setSelectedFile(null);
    } else {
      resetForm();
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingAbsence(null);
    resetForm();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!selectedUserId || !formData.date) {
      toast.error('Selecione um funcionário e informe a data');
      return;
    }

    const submitData = {
      user_id: selectedUserId,
      date: formData.date,
      type: formData.type,
      justified: formData.justified,
      notes: formData.notes || null,
    };

    if (formData.type === 'HOURS') {
      if (!formData.hours || Number(formData.hours) <= 0) {
        toast.error('Informe a quantidade de horas');
        return;
      }
      submitData.hours = Number(formData.hours);
      if (formData.period_start) submitData.period_start = formData.period_start;
      if (formData.period_end) submitData.period_end = formData.period_end;
    } else if (formData.type === 'DAYS') {
      if (!formData.days || Number(formData.days) <= 0) {
        toast.error('Informe a quantidade de dias');
        return;
      }
      submitData.days = Number(formData.days);
      if (formData.period_start) submitData.period_start = formData.period_start;
      if (formData.period_end) submitData.period_end = formData.period_end;
    }

    if (editingAbsence) {
      updateMutation.mutate({ id: editingAbsence.id, data: submitData, file: selectedFile });
    } else {
      createMutation.mutate({ data: submitData, file: selectedFile });
    }
  };

  const handleDelete = (absence) => {
    setDeleteAbsence(absence);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const datePart = dateStr.split('T')[0];
    const [year, month, day] = datePart.split('-');
    return `${day}/${month}/${year}`;
  };

  const getDayName = (dateStr) => {
    if (!dateStr) return '-';
    const datePart = dateStr.split('T')[0];
    const [year, month, day] = datePart.split('-');
    const date = new Date(year, month - 1, day);
    const days = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
    return days[date.getDay()];
  };

  // Calcular horas da jornada quando tipo for FULL_DAY
  const getDayScheduleHours = () => {
    if (formData.type !== 'FULL_DAY' || !formData.date || schedules.length === 0) return null;
    
    const date = new Date(formData.date);
    const dayOfWeek = date.getDay();
    const daySchedule = schedules.find(s => s.day_of_week === dayOfWeek);
    
    if (!daySchedule || !daySchedule.start_time || !daySchedule.end_time) return null;
    
    const [startHour, startMin] = daySchedule.start_time.split(':').map(Number);
    const [endHour, endMin] = daySchedule.end_time.split(':').map(Number);
    let totalMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);
    
    if (daySchedule.break_start_time && daySchedule.break_end_time) {
      const [breakStartHour, breakStartMin] = daySchedule.break_start_time.split(':').map(Number);
      const [breakEndHour, breakEndMin] = daySchedule.break_end_time.split(':').map(Number);
      const breakMinutes = (breakEndHour * 60 + breakEndMin) - (breakStartHour * 60 + breakStartMin);
      totalMinutes -= breakMinutes;
    }
    
    return (totalMinutes / 60).toFixed(2);
  };

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <AlertTriangle className="w-6 h-6" />
          Controle de Divergências/Faltas
        </h1>
        <p className="text-gray-600 mt-1">
          Registre divergências e faltas dos funcionários. Se justificada (abonada), não descontará do salário.
        </p>
      </div>

      {/* Filtros e botão de adicionar */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <User className="w-4 h-4 inline mr-1" />
              Funcionário
            </label>
            <select
              value={selectedUserId}
              onChange={(e) => {
                setSelectedUserId(e.target.value);
                resetForm();
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Selecione um funcionário</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.full_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              Mês
            </label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {monthNames.map((name, index) => (
                <option key={index + 1} value={index + 1}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Ano</label>
            <input
              type="number"
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              min="2020"
              max="2100"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                if (!selectedUserId) {
                  toast.error('Selecione um funcionário primeiro');
                  return;
                }
                handleOpenModal();
              }}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Nova Divergência
            </button>
          </div>
        </div>
      </div>

      {/* Lista de divergências */}
      {!selectedUserId ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center text-gray-500">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p>Selecione um funcionário para visualizar as divergências</p>
        </div>
      ) : isLoading ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          Carregando...
        </div>
      ) : absences.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p>Nenhuma divergência registrada para {monthNames[selectedMonth - 1]}/{selectedYear}</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Período
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Justificada
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Documento
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {absences.map((absence) => (
                <tr key={absence.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {formatDate(absence.date)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {getDayName(absence.date)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {absence.type === 'FULL_DAY' ? 'Dia Inteiro' :
                     absence.type === 'HOURS' ? 'Horas' : 'Dias'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {absence.type === 'FULL_DAY' && 'Dia inteiro'}
                    {absence.type === 'HOURS' && `${Number(absence.hours || 0).toFixed(2)}h`}
                    {absence.type === 'DAYS' && `${absence.days} dia(s)`}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {absence.justified ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Sim
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <XCircle className="w-3 h-3 mr-1" />
                        Não
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {absence.document_url ? (
                      <a
                        href={absence.document_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                      >
                        <FileText className="w-4 h-4" />
                        Ver documento
                      </a>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleOpenModal(absence)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Editar"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(absence)}
                        className="text-red-600 hover:text-red-900"
                        title="Deletar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de criar/editar divergência */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingAbsence ? 'Editar Divergência' : 'Nova Divergência'}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Data da Divergência *
                    </label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tipo de Divergência *
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="FULL_DAY">Dia Inteiro</option>
                      <option value="HOURS">Horas</option>
                      <option value="DAYS">Dias</option>
                    </select>
                    {formData.type === 'FULL_DAY' && formData.date && (
                      <p className="mt-1 text-xs text-gray-500">
                        {getDayScheduleHours() 
                          ? `Jornada do dia: ${getDayScheduleHours()}h (será descontado se não abonado)`
                          : 'Nenhuma jornada cadastrada para este dia da semana'}
                      </p>
                    )}
                  </div>

                  {formData.type === 'HOURS' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Quantidade de Horas *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={formData.hours}
                          onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
                          placeholder="Ex: 2.5"
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Início do Período (opcional)
                          </label>
                          <input
                            type="datetime-local"
                            value={formData.period_start}
                            onChange={(e) => setFormData({ ...formData, period_start: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Fim do Período (opcional)
                          </label>
                          <input
                            type="datetime-local"
                            value={formData.period_end}
                            onChange={(e) => setFormData({ ...formData, period_end: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {formData.type === 'DAYS' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Quantidade de Dias *
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={formData.days}
                          onChange={(e) => setFormData({ ...formData, days: e.target.value })}
                          placeholder="Ex: 3"
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Data Início (opcional)
                          </label>
                          <input
                            type="date"
                            value={formData.period_start ? formData.period_start.split('T')[0] : ''}
                            onChange={(e) => setFormData({ ...formData, period_start: e.target.value + 'T00:00' })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Data Fim (opcional)
                          </label>
                          <input
                            type="date"
                            value={formData.period_end ? formData.period_end.split('T')[0] : ''}
                            onChange={(e) => setFormData({ ...formData, period_end: e.target.value + 'T23:59' })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Upload className="w-4 h-4 inline mr-1" />
                      Comprovante (opcional)
                    </label>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      onChange={(e) => setSelectedFile(e.target.files[0] || null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    {editingAbsence?.document_url && !selectedFile && (
                      <p className="mt-1 text-xs text-gray-500">
                        Documento atual: <a href={editingAbsence.document_url} target="_blank" rel="noopener noreferrer" className="text-blue-600">Ver</a>
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Observações
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows="3"
                      placeholder="Ex: Atestado médico, motivo da falta, etc."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="justified"
                      checked={formData.justified}
                      onChange={(e) => setFormData({ ...formData, justified: e.target.checked })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="justified" className="ml-2 text-sm text-gray-700">
                      Justificada (Abonar) - Não descontará do salário
                    </label>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-xs text-yellow-800">
                      <strong>Importante:</strong> Se marcada como "Justificada", a divergência não será descontada do salário. 
                      Caso contrário, será descontado o período informado (dia completo, horas ou dias) do cálculo do holerite.
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                  disabled={createMutation.isLoading || updateMutation.isLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {createMutation.isLoading || updateMutation.isLoading ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
        </div>
      )}

      <ConfirmModal
        show={!!deleteAbsence}
        onConfirm={() => {
          if (deleteAbsence) {
            deleteMutation.mutate(deleteAbsence.id);
          }
          setDeleteAbsence(null);
        }}
        onCancel={() => setDeleteAbsence(null)}
        title="Excluir Divergência"
        message={deleteAbsence ? `Tem certeza que deseja deletar a divergência de ${deleteAbsence.user_name} do dia ${formatDate(deleteAbsence.date)}?` : ''}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />
    </div>
  );
};

export default AbsencesManagement;
